// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx (Field)
// See: .claude/skills/rehaut-design/HANDOFF.md
'use client';

import type { ReactNode } from 'react';

/**
 * Two-column "label / value" row used in the detail pane's identity
 * section. Label sits in a 120px gutter; value flexes. `mono` switches
 * the value to Geist Mono with tabular numerals for phone/dates/IDs.
 * `color` overrides the default ink (used for warnings, accent, etc.).
 *
 * Empty value renders an em-dash placeholder so labels never look
 * abandoned.
 */
export function Field({
  label,
  value,
  mono,
  color,
}: {
  label: string;
  value?: ReactNode;
  mono?: boolean;
  color?: string;
}) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div
      style={{
        display: 'grid',
        // The label gutter gives way (down to 72px) before the value
        // does, and the value may break long tokens (RFCs, one-word
        // vendor names) instead of running past a narrow pane's edge.
        gridTemplateColumns: 'minmax(72px, 120px) minmax(0, 1fr)',
        padding: '6px 28px',
        alignItems: 'baseline',
      }}
    >
      <div style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{label}</div>
      <div
        style={{
          fontSize: '13px',
          color: color || 'var(--ink)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {isEmpty ? <span style={{ color: 'var(--ink-5)' }}>—</span> : value}
      </div>
    </div>
  );
}
